import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { lookupProperty, lookupBroker } from "@/features/properties/api";

/** Normaliza o que o corretor digitou: tira espaços, força maiúsculas e garante o hífen do prefixo. */
function normalizeCode(raw: string): string {
  return raw.trim().toUpperCase().replace(/^(IMV|IMB)-?/, "$1-");
}

/**
 * Corpo do código público: começa em dígito e segue em dígito ou letra maiúscula.
 * Cobre o formato novo (`IMV-48217KY`, `IMB-48217K`) e o legado sequencial
 * (`IMV-000123`), que continua no banco e impresso em placa — placa não se corrige
 * com deploy. Aqui é filtro de lixo, não validação: quem diz se o código existe é a API.
 */
const CODE_BODY = "[0-9][0-9A-Z]{0,11}";
const PROPERTY_CODE = new RegExp(`^IMV-${CODE_BODY}$`);
const BROKER_CODE = new RegExp(`^IMB-${CODE_BODY}$`);

/**
 * Busca global (topbar): resolve códigos públicos e abre o recurso.
 * `IMV-` → imóvel do corretor (#129, via `/v1/properties/lookup`).
 * `IMB-` → corretor da plataforma (rede de parceiros, via `/v1/brokers/lookup`).
 */
export function GlobalSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const code = normalizeCode(value);
    if (!code) return;

    if (PROPERTY_CODE.test(code)) {
      setBusy(true);
      try {
        const hit = await lookupProperty(code);
        if (hit.found && hit.propertyId) {
          setValue("");
          navigate(`/properties/${hit.propertyId}`);
        } else {
          toast.error(t("topbar.propertyNotFound", { code }));
        }
      } finally {
        setBusy(false);
      }
      return;
    }

    if (BROKER_CODE.test(code)) {
      setBusy(true);
      try {
        const hit = await lookupBroker({ code });
        if (hit.found) {
          setValue("");
          toast.success(t("topbar.brokerFound", { name: hit.name ?? code }));
          navigate("/parceiros");
        } else {
          toast.error(t("topbar.brokerNotFound", { code }));
        }
      } finally {
        setBusy(false);
      }
      return;
    }

    toast.info(t("topbar.invalidCode"));
  }

  return (
    <form
      onSubmit={handleSubmit}
      role="search"
      className="hidden flex-1 items-center gap-2 rounded-xl border border-border px-3 py-2 text-ink-muted md:flex md:max-w-md"
    >
      {busy ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
      <input
        type="search"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={busy}
        placeholder={t("topbar.search")}
        aria-label={t("topbar.searchAria")}
        className="flex-1 bg-transparent text-sm outline-none placeholder:text-ink-soft disabled:opacity-60"
      />
    </form>
  );
}
